import React, { useEffect, useState } from "react";
import { Link, graphql, useStaticQuery } from "gatsby";
import LogoSprite from "./LogoSprite";
import Area from "./Area";
import {
  ChatBubbleLeftIcon,
  CheckIcon,
  EnvelopeOpenIcon,
} from "@heroicons/react/24/outline";
import useMatomoEvent from "./hooks/useMatomoEvent";
import FloatingWhatsAppButton from "./WhatsApp";

const EmailLink = ({ email }) => {
  const [emailVisible, setEmailVisible] = useState("");

  useEffect(() => {
    const [user, domain] = email.split("@");
    setEmailVisible(`${user}@${domain}`);
  }, [email]);

  return (
    <a href={`mailto:${emailVisible}`} className="py-1">
      {emailVisible}
    </a>
  );
};

const Footer = ({ title, phone, email }) => {
  const trackEvent = useMatomoEvent();

  const data = useStaticQuery(graphql`
    query FooterPages {
      allStrapiPage(sort: { order: ASC }) {
        nodes {
          id
          title
          slug
          order
        }
      }
    }
  `);

  let pages = data.allStrapiPage.nodes;

  // Agregar un nuevo elemento al array manualmente
  const extraItem = {
    id: "extra",
    title: "Cerramientos a Medida",
    slug: "",
  };

  pages = [...pages, extraItem];

  return (
    <footer className="bg-slate-700 text-slate-50 py-20">
      <div className="container">
        {/* Footer Top */}
        <div className="flex gap-16 sm:gap-12 md:gap-8 flex-col md:flex-row justify-between md:items-end pb-12 mb-12 border-b border-slate-600">
          <ul className="space-y-1 font-bold text-sm">
            <li>
              <LogoSprite
                id="logo-footer"
                className="w-40 aspect-[283/113] mb-12"
              />
            </li>
            <li className="flex gap-2 items-center">
              <EnvelopeOpenIcon className="size-4 stroke-amber-300" />
              <EmailLink email={email} />
            </li>
            <li className="flex gap-2 items-center">
              <ChatBubbleLeftIcon className="size-4 stroke-amber-300" />
              <a
                href={`tel:+34${phone.replace(/\s+/g, "")}`}
                onClick={() =>
                  trackEvent("Footer", "Clic en teléfono", `+34${phone}`)
                }
                className="py-1"
              >
                {`+34 ${phone}`}
              </a>
            </li>
          </ul>
          <div>
            <h2 className="uppercase text-sm mb-6 md:mb-8 font-semibold">
              Nuestros productos de cerramientos
            </h2>
            <ul className="columns-1 sm:columns-2 lg:columns-3 space-y-1 text-sm">
              {pages.map((page) => {
                const titleWithLocation = page.title.includes("en Madrid")
                  ? page.title
                  : `${page.title} en Madrid`;

                return (
                  <li key={page.id}>
                    <Link
                      to={`/${page.slug}`}
                      title={titleWithLocation}
                      className="flex items-center gap-1 py-1"
                      onClick={() =>
                        trackEvent("Footer", "Clic en página", page.title)
                      }
                    >
                      <CheckIcon className="size-3 stroke-amber-300" />
                      {page.title.replace(/ en Madrid$/i, "")}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* Footer Mid */}
        <div className="mb-12 text-xs">
          <h3 className="mb-4 uppercase font-semibold">
            Áreas de Servicio e Instalación de{" "}
            <Link
              to="/"
              title="Cerramientos en la Comunidad de Madrid"
              onClick={() =>
                trackEvent("Footer", "Clic en área de servicio", "Home")
              }
            >
              Cerramientos en la Comunidad de Madrid
            </Link>
          </h3>
          <Area />
        </div>

        {/* Bottom */}
        <div className="flex flex-col sm:flex-row sm:justify-between items-start sm:items-center gap-8">
          <p className="text-sm">
            Cerramientos Proyectop &copy; {new Date().getFullYear()}{" "}
            <Link rel="nofollow" to="/sitemap">
              Mapa Web
            </Link>{" "}
            |{" "}
            <Link rel="nofollow" to="/aviso-legal">
              Aviso Legal
            </Link>{" "}
            |{" "}
            <Link rel="nofollow" to="/politica-de-privacidad">
              Política de Privacidad
            </Link>{" "}
            |{" "}
            <Link rel="nofollow" to="/politica-de-cookies">
              Política de Cookies
            </Link>
          </p>
          <a
            href="//jatstudio.com"
            target="_blank"
            rel="noopener noreferrer nofollow"
            onClick={() => trackEvent("Footer", "Clic", "Jatstudio")}
          >
            <LogoSprite
              id="imago-jatstudio"
              className="size-8 fill-slate-100"
            />
            <span className="sr-only">
              Jatstudio Agencia de Marketing Digital
            </span>
          </a>
        </div>
      </div>

      <FloatingWhatsAppButton
        phone={phone}
        title={title}
        trackEvent={trackEvent}
      />
    </footer>
  );
};

export default Footer;
